import React from "react";

import Text from "@src/lib/ui/Text";
import TranslationModeContextProvider from "@src/features/libraryItem/course/contexts/TranslationModeContextProvider";
import { Button } from "@src/lib/ui/Button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@src/lib/ui/Tooltip";
import PinTackOutlineIcon from "@src/lib/icons/18px/pin-tack-outline";
import PinTackFillIcon from "@src/lib/icons/18px/pin-tack-fill";
import MessageBlastFileDropZone from "@src/features/messaging/MessageBlastFileDropZone";
import XIcon from "@src/lib/icons/18px/x";
import { SentMessage_MessageBlastMediaFragment } from "./SentMessage.generated";
import MessageBlastMediaPreview from "./MessageBlastMediaPreview";
import { sortMediaAttachments } from "./posts/mediaTiles";
import { useRouter } from "next/router";

type Props = {
  messageBlastMedias: Set<SentMessage_MessageBlastMediaFragment>;
  setMessageBlastMedias?: (
    newSet: Set<SentMessage_MessageBlastMediaFragment>,
  ) => void;
  allowMediaPreview?: boolean;
};

const MessageBlastAttachments: React.FC<Props> = ({
  allowMediaPreview = false,
  ...props
}) => {
  const router = useRouter();
  const messageBlastMediasArray = sortMediaAttachments(
    Array.from(props.messageBlastMedias),
  );

  return (
    <TranslationModeContextProvider>
      <TooltipProvider>
        <div className="flex flex-col gap-1 self-stretch">
          <Text variant="compact-title" className="mt-2">
            Attachments
          </Text>
          <div className="flex flex-col self-stretch">
            {messageBlastMediasArray.map((mbm) => {
              const title = mbm.libraryItem?.name.en || mbm.media?.mediaUrls.en;

              return (
                <div
                  key={mbm.id}
                  className="flex flex-1 items-center justify-between self-stretch border-b border-input bg-background py-1"
                >
                  <div className="flex flex-1 items-center gap-3">
                    <div className="size-[40px]">
                      <MessageBlastMediaPreview
                        id={mbm.id.toString()}
                        messageBlast={mbm}
                      />
                    </div>
                    <div className="flex-1">
                      <Text
                        variant="compact-title"
                        ellipsis
                        className="line-clamp-1"
                        onClick={() => {
                          if (!allowMediaPreview) return;

                          if (mbm.libraryItem) {
                            router.push({
                              pathname: "/library/library-item/[id]",
                              query: { id: mbm.libraryItem.id },
                            });
                          } else if (mbm.media) {
                            window.open(mbm.media.mediaUrls.en, "_blank");
                          }
                        }}
                      >
                        {title || "Untitled"}
                      </Text>
                    </div>
                  </div>
                  {props.setMessageBlastMedias && (
                    <div className="flex gap-2">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant={mbm.pinned ? "secondary" : "ghost"}
                            size="icon"
                            onClick={() => {
                              if (props.setMessageBlastMedias) {
                                const updatedArray = Array.from(
                                  props.messageBlastMedias,
                                ).map((x) => ({
                                  ...x,
                                  pinned: mbm === x ? !mbm.pinned : false,
                                }));
                                props.setMessageBlastMedias(
                                  new Set(updatedArray),
                                );
                              }
                            }}
                          >
                            {mbm.pinned ? (
                              <PinTackFillIcon className="text-muted-foreground" />
                            ) : (
                              <PinTackOutlineIcon className="text-muted-foreground" />
                            )}
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent side="top">
                          {mbm.pinned ? "Unpin from top" : "Pin to top"}
                        </TooltipContent>
                      </Tooltip>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => {
                              if (props.setMessageBlastMedias) {
                                const newSet = new Set(
                                  props.messageBlastMedias,
                                );
                                newSet.delete(mbm);
                                props.setMessageBlastMedias(newSet);
                              }
                            }}
                          >
                            <XIcon className="text-muted-foreground" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent side="top" className="max-w-64">
                          <Text variant="fine-print-description" multiline>
                            Remove attachment
                          </Text>
                        </TooltipContent>
                      </Tooltip>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          {props.setMessageBlastMedias && (
            <MessageBlastFileDropZone
              messageBlastMedias={props.messageBlastMedias}
              setMessageBlastMedias={props.setMessageBlastMedias}
            />
          )}
        </div>
      </TooltipProvider>
    </TranslationModeContextProvider>
  );
};

export default MessageBlastAttachments;
